import type { UploadProgress } from "components/common/UploadProgressToaster";
import UploadProgressToaster from "components/common/UploadProgressToaster";
import { useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { logToSentry } from "utils/sentry";

type UploadOptions = {
  file: File;
  presignedPostUrl: string;
  presignedPostFields: Record<string, string>;
  onComplete?: (s3Key: string) => void;
  onError?: (error: Error) => void;
};

// Marker set on errors that were already reported to Sentry at a specific
// detection site, so the catch-all handler can avoid logging them twice.
type ReportedError = Error & { reportedToSentry?: true };

export const useUploadWithProgress = () => {
  const progressRef = useRef<Map<string, UploadProgress>>(new Map());
  const xhrRef = useRef<Map<string, XMLHttpRequest>>(new Map());

  const dismissUpload = useCallback((id: string) => {
    toast.dismiss(id);
    progressRef.current.delete(id);
    xhrRef.current.delete(id);
  }, []);

  const cancelUpload = useCallback(
    (id: string) => {
      const progress = progressRef.current.get(id);
      if (!progress) return;

      const xhr = xhrRef.current.get(id);
      if (xhr) {
        xhr.abort();
        xhrRef.current.delete(id);
      }

      // update progress to cancelled status
      const cancelledProgress = {
        ...progress,
        status: "cancelled" as const,
      };
      progressRef.current.set(id, cancelledProgress);

      // re-render toast with cancelled state
      toast.custom(
        () => (
          <UploadProgressToaster
            upload={cancelledProgress}
            onDismiss={() => dismissUpload(id)}
          />
        ),
        { id, duration: Infinity },
      );

      // auto-dismiss after 3 seconds
      setTimeout(() => {
        dismissUpload(id);
      }, 3000);
    },
    [dismissUpload],
  );

  const startUpload = useCallback(
    async (options: UploadOptions): Promise<void> => {
      const {
        file,
        presignedPostUrl,
        presignedPostFields,
        onComplete,
        onError,
      } = options;
      const uploadId = `upload-${Date.now()}-${Math.random()}`;

      // initialize upload state
      let currentProgress: UploadProgress = {
        id: uploadId,
        filename: file.name,
        totalBytes: file.size,
        uploadedBytes: 0,
        percentage: 0,
        speedBytesPerSecond: 0,
        estimatedSecondsRemaining: 0,
        status: "uploading",
      };

      progressRef.current.set(uploadId, currentProgress);

      // helper to update progress and re-render toast
      const updateProgress = (updates: Partial<UploadProgress>) => {
        currentProgress = { ...currentProgress, ...updates };
        progressRef.current.set(uploadId, currentProgress);

        // re-render toast with updated progress
        toast.custom(
          () => (
            <UploadProgressToaster
              upload={currentProgress}
              onCancel={() => cancelUpload(uploadId)}
              onDismiss={() => dismissUpload(uploadId)}
            />
          ),
          { id: uploadId, duration: Infinity },
        );
      };

      // render initial toast
      updateProgress({});

      try {
        // prepare FormData for S3 presigned POST
        const formData = new FormData();

        // add all presigned post fields first (order matters for S3)
        Object.entries(presignedPostFields).forEach(([key, value]) => {
          formData.append(key, value);
        });

        // add the file last
        formData.append("file", file);

        // track upload progress
        const startTime = Date.now();
        let lastUpdateTime = startTime;
        let lastUploadedBytes = 0;

        // Every failure mode of a cross-origin POST to S3 surfaces the same way
        // (status 0, no readable response), so the message alone cannot tell a
        // blocked request from an expired upload policy from a dropped
        // connection. These fields are what separate them after the fact.
        const uploadDiagnostics = () => ({
          pageOrigin: window.location.origin,
          uploadHost: (() => {
            try {
              return new URL(presignedPostUrl).host;
            } catch {
              return "unparseable";
            }
          })(),
          elapsedMs: Date.now() - startTime,
          bytesSent: lastUploadedBytes,
          fileSize: file.size,
          fileType: file.type || "none",
          browserOnline: navigator.onLine,
        });

        // use XMLHttpRequest for upload progress tracking
        const xhr = new XMLHttpRequest();
        xhrRef.current.set(uploadId, xhr);

        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const uploadedBytes = event.loaded;
            const totalBytes = event.total;
            const percentage = Math.round((uploadedBytes / totalBytes) * 100);

            const now = Date.now();
            const elapsedSeconds = (now - startTime) / 1000;
            const timeSinceLastUpdate = (now - lastUpdateTime) / 1000;

            // update every 200ms or every 1MB
            if (
              timeSinceLastUpdate >= 0.2 ||
              uploadedBytes - lastUploadedBytes >= 1024 * 1024
            ) {
              const speed =
                elapsedSeconds > 0 ? uploadedBytes / elapsedSeconds : 0;
              const bytesRemaining = totalBytes - uploadedBytes;
              const eta = speed > 0 ? bytesRemaining / speed : 0;

              updateProgress({
                uploadedBytes,
                totalBytes,
                percentage,
                speedBytesPerSecond: Math.round(speed),
                estimatedSecondsRemaining: Math.round(eta),
              });

              lastUpdateTime = now;
              lastUploadedBytes = uploadedBytes;
            }
          }
        });

        // handle upload completion
        await new Promise<void>((resolve, reject) => {
          xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              const error: ReportedError = new Error(
                `Upload failed: ${xhr.statusText} (${xhr.status})`,
              );
              logToSentry("Upload failed - HTTP error", "error", {
                filename: file.name,
                ...uploadDiagnostics(),
                status: xhr.status,
                statusText: xhr.statusText,
                errorMessage: error.message,
              });
              error.reportedToSentry = true;
              reject(error);
            }
          });

          xhr.addEventListener("error", () => {
            const error: ReportedError = new Error(
              "Upload failed due to network error",
            );
            logToSentry("Upload failed - network error", "error", {
              filename: file.name,
              ...uploadDiagnostics(),
              errorMessage: error.message,
            });
            error.reportedToSentry = true;
            reject(error);
          });

          xhr.addEventListener("abort", () => {
            const abortError = new Error("Upload aborted");
            abortError.name = "AbortError";
            reject(abortError);
          });

          // send the request
          xhr.open("POST", presignedPostUrl);
          xhr.send(formData);
        });

        // mark as complete
        updateProgress({
          status: "complete",
          percentage: 100,
          uploadedBytes: file.size,
          totalBytes: file.size,
        });

        // auto-dismiss after 3 seconds
        setTimeout(() => {
          dismissUpload(uploadId);
        }, 3000);

        // extract s3_key from presigned post fields
        const s3Key = presignedPostFields["key"] || "";
        onComplete?.(s3Key);
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          // user cancelled, already handled
          return;
        }

        // log actual error for debugging, unless it was already reported at a
        // more specific detection site (e.g. the HTTP/network-error handlers)
        const alreadyReported =
          error instanceof Error && (error as ReportedError).reportedToSentry;
        if (!alreadyReported) {
          logToSentry("Upload error", "error", {
            filename: file.name,
            fileSize: file.size,
            errorMessage:
              error instanceof Error ? error.message : "Unknown error",
            errorType: error instanceof Error ? error.name : typeof error,
          });
        }

        // show friendly message to user
        const friendlyMessage = "Unable to upload file. Please try again.";

        updateProgress({
          status: "error",
          errorMessage: friendlyMessage,
        });

        onError?.(error instanceof Error ? error : new Error("Upload failed"));
      } finally {
        xhrRef.current.delete(uploadId);
      }
    },
    [cancelUpload, dismissUpload],
  );

  return {
    startUpload,
    cancelUpload,
  };
};
